import React, { ReactElement, ReactNode } from 'react'
import { Col, ColProps, Form, FormItemProps, Row } from 'antd'

import { TFormSectionBreakPoint, useFormSectionContext } from './FormSection'
import { useFormFnUIContext } from './FormUI'

export type TFormItemUISize = 'small' | 'medium' | 'large' | 'x-large'
export type TSizePreset = 'select' | 'input' | 'location' | 'textArea'

const sizePresetSizeMap: Record<TSizePreset, TFormItemUISize> = {
  input: 'small',
  location: 'large',
  select: 'small',
  textArea: 'large',
}

const colBreakpointSpan: Record<
  TFormItemUISize,
  Record<TFormSectionBreakPoint, ColProps['span']>
> = {
  small: {
    XS: 24,
    SM: 12,
    MD: 8,
    LG: 4,
  },
  medium: {
    XS: 24,
    SM: 24,
    MD: 12,
    LG: 6,
  },
  large: {
    XS: 24,
    SM: 24,
    MD: 24,
    LG: 12,
  },
  'x-large': {
    XS: 24,
    SM: 24,
    MD: 24,
    LG: 24,
  },
}

export type FormItemUIProps = FormItemProps & {
  disableColWrapped?: boolean
  addonAfterLabel?: ReactNode
  /**
   * @deprecated
   * use size or size preset to define the size
   */
  colProps?: ColProps
  size?: TFormItemUISize
  sizePreset?: TSizePreset
  hideLabel?: boolean
  childWrapper?: ({
    children,
  }: {
    children: React.ReactNode
  }) => JSX.Element | null
  withLabel?: boolean
  singleItemInRow?: boolean
} & (
    | {
        disableColWrapped: true
        colProps?: never
      }
    | {
        disableColWrapped?: false
        colProps?: ColProps
      }
  )

const DefaultChildWrapper = ({ children }: { children: React.ReactNode }) => (
  <>{children}</>
)

export const FormItemUI = ({
  children,
  colProps,
  addonAfterLabel,
  disableColWrapped,
  label,
  withLabel = true,
  hidden,
  size: sizeFromProps,
  childWrapper: ChildWrapper = DefaultChildWrapper,
  sizePreset,
  singleItemInRow,
  ...props
}: FormItemUIProps) => {
  const { disabled } = useFormFnUIContext()
  const { breakpoint } = useFormSectionContext()

  const sizeFromPreset = sizePreset && sizePresetSizeMap[sizePreset]
  const size = sizeFromProps || sizeFromPreset || 'medium'
  const colBreakpointSpanData = colBreakpointSpan[size]

  const ui = (
    <Form.Item
      label={
        withLabel && (
          <Row gutter={8} align="middle">
            <Col>{label}</Col>
            {addonAfterLabel && <Col>{addonAfterLabel}</Col>}
          </Row>
        )
      }
      hidden={hidden}
      {...props}
    >
      {typeof children === 'function'
        ? children
        : React.cloneElement(children as ReactElement, {
            disabled:
              disabled ||
              (children as ReactElement<{ disabled?: boolean }>)?.props
                ?.disabled,
          })}
    </Form.Item>
  )

  if (disableColWrapped || hidden) {
    return <ChildWrapper>{ui}</ChildWrapper>
  }

  const col = (
    <Col span={colBreakpointSpanData[breakpoint] ?? 24} {...colProps}>
      <ChildWrapper>{ui}</ChildWrapper>
    </Col>
  )

  if (singleItemInRow) {
    return (
      <Col xs={24}>
        <Row>{col}</Row>
      </Col>
    )
  }

  return col
}

export default FormItemUI
